import { Injectable } from '@angular/core';
import { WorkFlowService } from './work-flow.service';
@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  public pageOrder: any = []; // Array to store the pages
  public currentFlowObjectIndex: number = 0; //step
  public currentPageId: number = 0; //id of page
  public subFlowIndex: number = -1;
  private currentSubFlow: any;
  public currentPageIndex: number = 0;
  private flowObject: any;
  private mainFlowSize: number = 0;
  public subFlowWorking: boolean = false;
  public is_resume: boolean = false;
  private maxFlowObjectIndex: number = 0;


  constructor(private workFlowService: WorkFlowService) {
    this.workFlowService.flowObject$.subscribe((flowObject) => {
      if (flowObject) {
        this.flowObject = flowObject;
        this.mainFlowSize = Object.keys(flowObject).filter((key) => /^\d+$/.test(key)).length; // Match only integer keys
      }
    });
  }

  getCurrentFlow() {
    if (this.subFlowWorking) {
      return this.currentSubFlow;
    }
    return this.flowObject[this.currentFlowObjectIndex];
  }

  // #Set Flow----------------------------------------------------
  setCurrentFlowObjectByFlowIndex(
    flow_index: number = this.currentFlowObjectIndex + 1,
    page_index: number = 0,
  ) {
    this.currentFlowObjectIndex = flow_index;
    if (this.currentFlowObjectIndex > this.maxFlowObjectIndex) {
      this.maxFlowObjectIndex = this.currentFlowObjectIndex;
    }
    this.resetCurrentPageIndex(page_index);
    if (this.currentFlowObjectIndex >= this.mainFlowSize) {
      this.goToRootFlow();
    }
  }

  setFlowByIndex(flow_index: number, page_index: number = 0) {
    this.currentFlowObjectIndex = flow_index;
    this.setPageOrder(page_index, this.flowObject[flow_index]);
  }

  //---------------------------------------------------------------

  getFlowByIndex(index: string | number) {
    return this.flowObject[index];
  }

  getRootFlow() {
    return this.flowObject['root'];
  }

  getResumeStateFlow() {
    return this.flowObject['resume'];
  }

  getSuccessFlow() {
    return this.flowObject['success'];
  }

  getFailFlow() {
    return this.flowObject['fail'];
  }

  getCancelRegisterFlow() {
    return this.flowObject['cancel-register'];
  }

  async getRootPage() {
    return this.workFlowService.getPageComponentByID(this.pageOrder[0]);
  }

  async getCurrentPage() {
    return this.getPageComponentByID(this.currentPageId);
  }

  getLastPageID(step_id: string) {
    const steps = this.getCurrentFlow();
    const matchedSteps = steps.filter((step: any) => step.step_key === step_id);

    if (matchedSteps.length > 0) {
      // Get the last matching step and return its page_id
      return matchedSteps[matchedSteps.length - 1].page_id;
    }

    return null;
  }

  async getPageComponentByID(id: number) {
    const component = await this.workFlowService.getPageComponentByID(id);
    return component;
  }

  getPageIndexWithPageID(step_id: string | number, page_id: number) {
    const step = this.flowObject[step_id];
    if (!step) return null;
    for (let index = 0; index < step.length; index++) {
      if (step[index].page_id == page_id) {

        return index; // Return the index of the page
      }
    }
    return null;
  }

  getPageIndexNextStep(step_key: string) {
    const nextLevel = parseFloat((Number(step_key) + 0.1).toFixed(1)).toString();
    const flow: any = this.getCurrentFlow();
    const index = flow.findIndex((step: any) => step.step_key === nextLevel);
    return index;
  }

  findFirstPageIndex(stepKey: string): number {
    const current_flow = this.getCurrentFlow();
    const index = current_flow.findIndex((item: any) => item.step_key === stepKey);
    return index !== -1 ? index : 0;
  }

  setCurrentPageIndex(index: number) {
    this.currentPageIndex = index;
  }

  //set current page id for stepper
  setCurrentPageId(page_id: number) {
    this.currentPageId = page_id;
  }

  //update current_step_id and current_page_id to backend
  async checkPoint() {
    if (this.subFlowWorking)
      return;
    const latest_step = this.getCurrentPageStepKey();
    if (latest_step != 'root' && latest_step != 'checkPoint') {
      await this.workFlowService.updateCurrentStepIDAndPageID(latest_step, this.currentPageId);
    }
  }

  isCurrentFlowSuccess(flow_index: number, step_key: string, page_id: number) {
    const page_order = this.getFlowByIndex(flow_index);
    const last_page = page_order[page_order.length - 1];
    if (last_page.page_id === page_id && last_page.step_key === step_key) {
      return true
    }
    return false;
  }

  // Set the order of pages
  async setPageOrder(page_index: number = 0, flow: any = this.getCurrentFlow()) {
    const pages = Object.values(flow).map((page: any) => {
      if (typeof page === 'object') {
        return page.page_id;
      }
      return page;
    });

    this.pageOrder = pages.filter((component) => component !== undefined);
    this.currentPageIndex = page_index;
    if (this.pageOrder.length > 0) {
      this.setCurrentPageId(this.pageOrder[page_index]);
    }

  }

  resetCurrentPageIndex(page_index: number = 0) {
    this.currentPageIndex = page_index; // Reset index when setting a new order
  }

  // Get the current page index
  getCurrentPageIndex(): number {
    return this.currentPageIndex;
  }

  getCurrentPageObject() {
    const flow = this.getCurrentFlow();
    const page = Object.values(flow).find(
      (page: any, index: number) =>
        page?.page_id === this.currentPageId && index === this.currentPageIndex,
    );
    return page;
  }

  getCurrentPageStepKey() {
    const page: any = this.getCurrentPageObject();
    return page?.step_key;
  }

  getCurrentPageParams() {
    const page: any = this.getCurrentPageObject();
    return page?.params || undefined;
  }

  getCurrentState() {
    const page: any = this.getCurrentPageObject();
    return page?.state || undefined;
  }

  canResume() {
    const current_flow = this.getCurrentFlow();
    try {
      const current_page_obj = current_flow[this.currentPageIndex];
      return current_page_obj?.can_resume;
    } catch (error) {
      return undefined;
    }
  }

  // Get the next page in the order
  async getNextPage() {
    if (this.currentPageIndex < this.pageOrder.length - 1) {
      this.currentPageIndex++;
      this.setCurrentPageId(this.pageOrder[this.currentPageIndex]);
      return this.getCurrentPage();
    }
    return null;
  }

  async getSkipNextPage() {
    if (this.currentPageIndex < this.pageOrder.length - 2) {
      this.currentPageIndex += 2;
      this.setCurrentPageId(this.pageOrder[this.currentPageIndex]);
      return this.getCurrentPage();
    }
    return null;
  }

  // Get the previous page in the order
  async getPreviousPage() {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--;
      this.setCurrentPageId(this.pageOrder[this.currentPageIndex]);
      return this.getCurrentPage(); // .name to get current page name
    }
    return null; // No more pages to navigate back
  }

  // Check if there is a next page
  canGoForward(): boolean {
    return this.currentPageIndex < this.pageOrder.length - 1;
  }

  canSkipForward(): boolean {
    return this.currentPageIndex < this.pageOrder.length - 2;
  }

  // Check if there is a previous page
  canGoBack(): boolean {
    return this.currentPageIndex > 0;
  }

  goToRootFlow() {
    this.setPageOrder(0, this.getRootFlow());
    this.resetSubFlowIndex();
  }

  goToResumeStateFlow() {
    this.setPageOrder(0, this.getResumeStateFlow());
  }

  goToSuccessFlow() {
    this.setPageOrder(0, this.getSuccessFlow());
  }

  goToFailFlow() {
    this.setPageOrder(0, this.getFailFlow());
  }

  goToCancelRegisterFlow() {
    this.setPageOrder(0, this.getCancelRegisterFlow());
  }

  async updateState() {
    await this.workFlowService.updatePassStep(this.currentFlowObjectIndex, this.maxFlowObjectIndex);
  }


  //---------------handle sub flow-------------
  setSubFlowIndex(index?: number) {
    if (index) {
      this.subFlowIndex = index;
    } else {
      this.subFlowIndex += 1;
    }
  }

  setSubFlowWorking(value: boolean) {
    if (value === true) {
      const current_flow = this.getCurrentFlow();
      const page_obj = current_flow[this.currentPageIndex];
      this.currentSubFlow = page_obj.sub_flow[this.subFlowIndex].pages;
      this.subFlowWorking = true;
    } else {
      this.subFlowWorking = false;
    }
  }

  resetSubFlowIndex() {
    this.subFlowIndex = -1;
  }

  isPassAll() {
    //this.mainflowSize-1 : get index 0 out;
    return this.currentFlowObjectIndex >= this.mainFlowSize - 1 ? true : false;
  }
}
